<template>
  <div :class="classnames">
    <div class="category-selector__container max-w-256 lg:mx-auto">
      <div
        :class="`category-selector__chips mx-4 ${areAllCategoriesVisible ? 'category-selector__chips--visible' : ''}`"
      >
        <chip
          :key="category.slug"
          v-for="category in categoriesList"
          :slug="category.slug"
          :sprite="category.sprite"
          :label="category.title"
          @click="$emit('categoryChange', category)"
          :active="activeCategory && activeCategory.slug === category.slug"
          class="category-selector__chip"
          :background-color="chipColor"
          :data-slug="category.slug"
        />
      </div>
      <button
        v-if="!collapsedCategoriesOnMobile"
        class="category-selector__view-all"
        @click="toggleCategoriesVisiblity"
      >
        <span>{{ showAllCategoriesLabel }}</span>
        <chevron-icon color="blue" :direction="areAllCategoriesVisible ? 'top' : 'bottom'" size="s" />
      </button>
    </div>
  </div>
</template>

<script>
import Chip from "@/component-library/chip"
import ChevronIcon from "@/component-library/chevron-icon"
import bemBlockModifiers from "@/utils/bem-block-modifiers"

export default {
  name: "CategorySeleector",
  components: {
    Chip,
    ChevronIcon
  },
  props: {
    categories: { type: Array, required: true },
    activeCategory: {
      type: Object,
      required: false,
      default: null
    },
    chipColor: { type: String, required: true },
    modifiers: { type: Array, default: () => {} },
    collapsedCategoriesOnMobile: { type: Boolean, default: false }
  },
  data() {
    return {
      areAllCategoriesVisible: false,
      mobileVisibilityState: false
    }
  },
  computed: {
    categoriesList() {
      const categoriesEnd = this.areAllCategoriesVisible === true ? this.categories.length : 10
      return this.categories.slice(0, categoriesEnd)
    },
    showAllCategoriesLabel() {
      return this.areAllCategoriesVisible ? this.$t("hideAllCategories") : this.$t("showAllCategories")
    },
    classnames() {
      return bemBlockModifiers("category-selector", this.modifiers.concat(this.collapsedClass))
    },
    collapsedClass() {
      return this.collapsedCategoriesOnMobile ? "sm-collapsed" : ""
    }
  },
  mounted() {
    this.adjustCategoriesVisibility()
    window.addEventListener("resize", this.adjustCategoriesVisibility)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.adjustCategoriesVisibility)
  },
  methods: {
    adjustCategoriesVisibility() {
      const biggerThanSMatcher = window.matchMedia("(min-width: 767px)")
      if (this.collapsedCategoriesOnMobile) {
        // if we display the scrollable-to-right list instead of one category per row, then we want to display
        // all categories so the user doesn't get confused by having both a button to load more + scrolling to the right.
        this.areAllCategoriesVisible = true
      } else {
        this.areAllCategoriesVisible = biggerThanSMatcher.matches ? true : this.mobileVisibilityState
      }
    },
    toggleCategoriesVisiblity() {
      this.mobileVisibilityState = !this.areAllCategoriesVisible
      this.areAllCategoriesVisible = !this.areAllCategoriesVisible
    }
  },
  i18n: {
    messages: {
      en: {
        showAllCategories: "Show all",
        hideAllCategories: "Hide additional categories"
      },
      de: {
        showAllCategories: "Alle Kategorien anzeigen",
        hideAllCategories: "Zusätzliche Kategorien ausblenden"
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.category-selector {
  $root: &;

  &__chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-content: center;
    gap: $spacing-8;

    margin-bottom: $spacing-16;

    @include media(min-width, m) {
      display: inline-flex;
    }
  }

  &__view-all {
    display: block;
    margin: 0 auto;

    color: $blue;
    text-decoration: underline;

    @include media(min-width, m) {
      display: none;
    }
  }

  &__chip {
    display: flex;
    align-items: flex-start;
    width: 100%;

    @include media(min-width, m) {
      display: inline-flex;
      align-items: center;
      width: auto;

      &:nth-child(n + 10) {
        display: inline-flex;
      }
    }
  }

  &--left-aligned {
    #{$root}__chips {
      justify-content: flex-start;
    }
  }

  @include media(max-width, m) {
    &--sm-collapsed {
      #{$root}__container {
        max-width: 100vw;
        overflow-x: scroll;
      }

      #{$root}__chips {
        // "Simulate" a bigger space (than viewport) for the chips to be scrollable.
        min-width: 1200px;
        max-width: 1200px;
      }

      #{$root}__chip {
        width: auto;
      }
    }
  }
}
</style>
